/**
 * Shell del portal: la barra de secciones y la vista activa.
 *
 * La sección se refleja en el hash para que un enlace directo a
 * `#asistencia` abra la pestaña correcta, igual que en el front anterior.
 *
 * Las vistas NO se desmontan al cambiar de sección: se ocultan. Asistencia al
 * Viaje tarda decenas de segundos en traer una consulta, y desmontarla perdía
 * el resultado y disparaba la búsqueda de nuevo al volver. Ahora una consulta
 * se rehace solo cuando alguien aprieta Buscar (pedido del 15-09).
 *
 * Se montan a demanda —solo las secciones ya visitadas— para no pagar al
 * arranque el costo de las tres.
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, LogOut } from 'lucide-react';

import { ETIQUETA_ROL } from './api';
import { esClaveSeccion, SECCIONES, type ClaveSeccion } from './navigation';
import { SesionProvider } from './components/SesionProvider';
import { useSesion } from './components/useSesion';
import { CambioClave } from './views/asistencia/CambioClave';
import { Ingreso } from './views/asistencia/Ingreso';
import { VistaAsistencia } from './views/asistencia/VistaAsistencia';
import { VistaConfiguracion } from './views/configuracion/VistaConfiguracion';
import { VistaNovedades } from './views/novedades/VistaNovedades';
import { VistaQuilmes } from './views/quilmes/VistaQuilmes';
import { VistaUsuarios } from './views/usuarios/VistaUsuarios';

function seccionDelHash(): ClaveSeccion {
  const bruto = window.location.hash.replace('#', '');
  return esClaveSeccion(bruto) ? bruto : 'novedades';
}

export function App(): React.JSX.Element {
  // El provider envuelve todo el portal —y no solo Asistencia— porque la barra
  // necesita el rol para saber si dibuja la sección de Usuarios.
  return (
    <SesionProvider>
      <Portal />
    </SesionProvider>
  );
}

function Portal(): React.JSX.Element {
  const { sesion, modulo, cargando, salir, recargarSesion } = useSesion();
  const [seccion, setSeccion] = useState<ClaveSeccion>(seccionDelHash);
  /** Secciones ya abiertas: se quedan montadas para conservar su estado. */
  const [visitadas, setVisitadas] = useState<ReadonlySet<ClaveSeccion>>(
    () => new Set([seccionDelHash()]),
  );

  const esAdministrador = modulo?.sinLogin === true || sesion?.rol === 'admin';

  const ir = useCallback((clave: ClaveSeccion) => {
    setSeccion(clave);
    setVisitadas((previas) => (previas.has(clave) ? previas : new Set([...previas, clave])));
    if (window.location.hash !== `#${clave}`) {
      window.history.replaceState(null, '', `#${clave}`);
    }
  }, []);

  // El botón de atrás del navegador tiene que cambiar de pestaña.
  useEffect(() => {
    const alCambiar = (): void => ir(seccionDelHash());
    window.addEventListener('hashchange', alCambiar);
    return () => window.removeEventListener('hashchange', alCambiar);
  }, [ir]);

  /*
   * Puerta del portal. Va acá y no dentro de una pestaña: con tres solapas,
   * tener el ingreso adentro de Asistencia hacía que el portal abriera en
   * Novedades sin pedir credenciales nunca.
   *
   * Si el módulo no está habilitado no se pide nada: sin configuración no hay
   * padrón contra el cual validar, y la pestaña explica qué falta.
   */
  if (cargando) {
    return (
      <div className="asistencia cargando">
        <Loader2 className="girando" size={20} aria-hidden="true" />
        Cargando el portal…
      </div>
    );
  }

  if (modulo?.habilitado === true && modulo.sinLogin !== true) {
    if (!sesion) {
      return (
        <main className="puerta">
          <Ingreso />
        </main>
      );
    }

    /*
     * Clave provisoria: no se entra hasta cambiarla. La puso un administrador
     * y por lo tanto la conoce alguien más; operar con ella haría que la
     * auditoría atribuya a esta persona algo que otro pudo hacer.
     */
    if (sesion.debeCambiarClave === true) {
      return (
        <main className="puerta">
          <CambioClave
            usuario={sesion.usuario}
            onListo={() => void recargarSesion()}
            onSalir={() => void salir()}
          />
        </main>
      );
    }
  }

  const visibles = SECCIONES.filter((s) => s.rolMinimo !== 'admin' || esAdministrador);

  /*
   * Si alguien llega con `#usuarios` sin ser Administrador, la sección no
   * existe para él: se lo manda a la primera visible en vez de dejar la
   * pantalla en blanco.
   */
  const activa = visibles.some((s) => s.clave === seccion) ? seccion : 'novedades';

  return (
    <>
      <nav className="barra-secciones" aria-label="Secciones del portal">
        {/*
          La marca, separada de las solapas por una línea: dice de quién es el
          portal. El símbolo es navy, así que nunca va sobre el navy.
        */}
        <div className="barra-marca">
          <img src="/simbolo-azul.png" alt="CodyLog" />
          Portal RRHH
        </div>
        {visibles.map(({ clave, etiqueta, icono: Icono }) => (
          <button
            key={clave}
            type="button"
            className={`pestania${activa === clave ? ' activa' : ''}`}
            {...(activa === clave ? { 'aria-current': 'page' as const } : {})}
            onClick={() => ir(clave)}
          >
            <Icono size={16} aria-hidden="true" />
            {etiqueta}
          </button>
        ))}
        {/*
          Quién está usando el portal, y cómo salir. Va en la barra y no dentro
          de una pestaña: acompaña a las tres.
        */}
        <div className="sesion">
          {sesion && (
            <span title={sesion.nombre ?? undefined}>
              {`${sesion.nombre?.trim() || sesion.usuario} · ${ETIQUETA_ROL[sesion.rol]}`}
            </span>
          )}
          {/* Sin login no hay sesión que cerrar: el botón mentiría. */}
          {modulo?.sinLogin !== true && sesion && (
            <button type="button" className="plano" onClick={() => void salir()}>
              <LogOut size={14} aria-hidden="true" />
              Salir
            </button>
          )}
          <span className="version" title={`Compilado ${__BUILD_TIME__}`}>
            v{__APP_VERSION__}
          </span>
        </div>
      </nav>

      <main>
        {/*
          `hidden` y no desmontar: lo que ya se consultó sigue ahí al volver.
          Cada sección se monta la primera vez que se la abre.
        */}
        {visitadas.has('novedades') && (
          <div hidden={activa !== 'novedades'}>
            <VistaNovedades />
          </div>
        )}
        {visitadas.has('asistencia') && (
          <div hidden={activa !== 'asistencia'}>
            <VistaAsistencia />
          </div>
        )}
        {visitadas.has('quilmes') && (
          <div hidden={activa !== 'quilmes'}>
            <VistaQuilmes />
          </div>
        )}
        {visitadas.has('configuracion') && (
          <div hidden={activa !== 'configuracion'}>
            <VistaConfiguracion />
          </div>
        )}
        {visitadas.has('usuarios') && esAdministrador && (
          <div hidden={activa !== 'usuarios'}>
            <VistaUsuarios />
          </div>
        )}
      </main>
    </>
  );
}
